// WDCE-199 step 9g-3b (D56): what the EFTPOS panel does, kept out of the React code so it can be tested with
// bin/js-test. useEftposPayment.js and FoodTableOrder.jsx only call these, with the browser's own fetch, timers and
// document.
//
// - connectPanel: the panel's session (paymentSession.js, eftposApi.js) for one order. The page-load lookup, waking it
//   when the tab is visible again, and telling the screen each new state, when a key request is out or done with, and
//   when a payment it was following is approved (panelView's reachedApproval). stop() ends it. Not enabled
//   (panelView's paneEnabled), it does nothing at all: no session, no request, no listener and no timer.
// - createPayButton: Pay. The checkout PATCH (payment type eftpos) through withOrder's handleOrderUpdate, with 9e's
//   handling of what comes back, unless part is already paid (9g-2 refuses it then); then the start, on the PIN pad
//   shown, with process_and_print as a real true or false. The checkout is sent (reading the form) before the screen
//   is told it's being saved, which locks the form. A second press while the checkout is being saved, or a press
//   while Pay isn't enabled, is ignored.

import { createEftposApi } from './eftposApi'
import { createPaymentSession } from './paymentSession'
import { initialState } from './paymentScreen'
import { reachedApproval } from './panelView'
import { checkoutErrorSteps } from '../orders/orderResponse'

const DISCONNECTED = Object.freeze({ pay() {}, pressKey() {}, stop() {} })

export function connectPanel({
  enabled, orderId, fetch, csrfToken, setTimeout, clearTimeout, document, onState, onKeyBusy, onApprovedHere
}) {
  if (!enabled) return DISCONNECTED

  let previous = initialState()
  const session = createPaymentSession({
    api: createEftposApi({ fetch, csrfToken, orderId }),
    setTimeout,
    clearTimeout,
    onChange: (state) => {
      const approvedHere = reachedApproval(previous, state)
      previous = state
      onState(state)
      if (approvedHere) onApprovedHere()
    },
    onKeyBusyChange: onKeyBusy
  })
  const wakeWhenVisible = () => {
    if (document.visibilityState === 'visible') session.wake()
  }
  document.addEventListener('visibilitychange', wakeWhenVisible)
  session.lookup()

  return {
    pay: (args) => session.pay(args),
    pressKey: (key, data) => session.pressKey(key, data),
    stop() {
      document.removeEventListener('visibilitychange', wakeWhenVisible)
      session.stop()
    }
  }
}

// onCheckingOut(busy) hears when the checkout PATCH goes out and comes back. The returned press resolves with whether
// the start was asked for.
export function createPayButton({ onCheckingOut }) {
  let checkingOut = false

  const done = (busy) => {
    checkingOut = busy
    onCheckingOut(busy)
  }

  return function press({ view, handleOrderUpdate, pay, eftposTerminal, processAndPrint, alert, reload }) {
    if (checkingOut || !view || !view.pay.enabled) return Promise.resolve(false)

    const start = () => {
      pay({ eftposTerminalId: eftposTerminal.id, processAndPrint: processAndPrint === true })
      return true
    }
    if (!view.pay.checkoutFirst) return Promise.resolve(start())

    // Step 9g-4b: onCheckingOut(true) locks the checkout (its fieldset), and disabled fields aren't sent with a
    // form, so the checkout is sent first: withOrder's handleOrderUpdate reads the form as soon as it's called.
    // checkingOut is set before, so the checkout can't be sent twice.
    checkingOut = true
    let sent
    try {
      sent = Promise.resolve(handleOrderUpdate({ checkout: true, paymentType: 'eftpos' }))
    } catch (error) {
      sent = Promise.reject(error)
    }
    onCheckingOut(true)
    return sent.then((outcome) => {
      done(false)
      // withOrder has shown anything but ok, and reloads or leaves the page if the outcome says to (9e).
      if (!outcome || outcome.kind !== 'ok') return false

      // As OrderForm's checkout does.
      const errorSteps = checkoutErrorSteps(outcome.order.errors)
      if (errorSteps.length > 0) {
        errorSteps.forEach((step) => (step.reload ? reload() : alert(step.alert)))
        return false
      }
      return start()
    }, (error) => {
      done(false)
      console.error('EFTPOS checkout failed in the browser', error)
      return false
    })
  }
}
